import { observer } from 'mobx-react-lite';
import {
    SaveAllFilesToOneDriveConsumerText,
    SaveAllFilesToGroupFilesText,
    SaveAllFilesToOneDriveText,
} from './SaveAllToCloudView.locstring.json';
import { CancelText } from 'owa-locstrings/lib/strings/canceltext.locstring.json';
import loc, { format } from 'owa-localize';
import getSaveAllToCloudStatusLabel from '../utils/getSaveAllToCloudStatusLabel';
import { CommandButton } from '@fluentui/react/lib/Button';
import { logUsage } from 'owa-analytics';
import { getCompanyName } from 'owa-attachment-data';
import {
    ActionMessageId,
    ActionType,
    AttachmentFullViewState,
    isSaveToCloudApplicable,
    setOngoingActionAndActionMessage,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
import { cancelSaveAttachmentsToCloud } from 'owa-attachment-savetocloud';
import saveAllToCloud from 'owa-attachment-well-data/lib/actions/saveAllToCloud';
import setSaveAllToCloudStatus from 'owa-attachment-well-data/lib/actions/setSaveAllToCloudStatus';
import AttachmentWellViewState from 'owa-attachment-well-data/lib/schema/AttachmentWellViewState';
import SaveAllToCloudStatus from 'owa-attachment-well-data/lib/schema/SaveAllToCloudStatus';
import cancelSaveAttachmentsToCloudService from 'owa-attachment-well-data/lib/services/cancelSaveAttachmentsToCloud';
import getAttachmentViewStateFromAttachmentWell from 'owa-attachment-well-data/lib/utils/getAttachmentViewStateFromAttachmentWell';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { lazyUnsubscribe, NotificationCallback, NotificationSubscription } from 'owa-notification';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getMailboxRequestOptions } from 'owa-request-options-types';

import * as React from 'react';

export interface SaveAllToCloudProps {
    attachmentWellViewState: AttachmentWellViewState;
    parentItemId: ClientItemId;
    sxsId: string;
}

const styles = require('./AttachmentWellBarView.scss');
const classNames = require('classnames');

// This component is responsible for showing the save all to cloud view
export default observer(function SaveAllToCloudView(props: SaveAllToCloudProps) {
    const operationId = React.useRef<string>();
    const subscription = React.useRef<NotificationSubscription>();
    const notificationCallback = React.useRef<NotificationCallback>();
    const attachmentsToSaveToCloud = React.useRef<ClientAttachmentId[]>();
    const shouldDisplaySaveAllToCloudButton = (isSaveAllToCloudApplicable: boolean): boolean => {
        if (!isSaveAllToCloudApplicable) {
            return false;
        }
        if (props.attachmentWellViewState.saveAllToCloudStatus != SaveAllToCloudStatus.Inactive) {
            return false;
        }
        if (props.parentItemId.mailboxInfo?.type == 'GroupMailbox' && isConsumer()) {
            return false;
        }
        return true;
    };
    const getSaveAllToCloudLabel = (): string => {
        const isGroup: boolean = props.parentItemId.mailboxInfo?.type === 'GroupMailbox';
        if (isConsumer()) {
            return loc(SaveAllFilesToOneDriveConsumerText);
        } else if (isGroup) {
            return loc(SaveAllFilesToGroupFilesText);
        } else {
            const formatString = loc(SaveAllFilesToOneDriveText);
            return format(formatString, getCompanyName());
        }
    };
    const shouldDisplayStatusLabel = (): boolean => {
        const saveAllToCloudStatus = props.attachmentWellViewState.saveAllToCloudStatus;
        return (
            saveAllToCloudStatus === SaveAllToCloudStatus.InProgress ||
            saveAllToCloudStatus === SaveAllToCloudStatus.Failed ||
            saveAllToCloudStatus === SaveAllToCloudStatus.Completed ||
            saveAllToCloudStatus === SaveAllToCloudStatus.Canceled
        );
    };
    const getSaveAllToCloudStatusLabel_0 = (): string => {
        return getSaveAllToCloudStatusLabel(
            props.parentItemId.mailboxInfo.mailboxSmtpAddress,
            props.parentItemId.mailboxInfo?.type === 'GroupMailbox',
            props.attachmentWellViewState.saveAllToCloudStatus
        );
    };
    const shouldDisplayCancelButton = (): boolean => {
        return (
            props.attachmentWellViewState.saveAllToCloudStatus === SaveAllToCloudStatus.InProgress
        );
    };
    const onClickSaveToCloudButton = async (ev: any) => {
        setSaveAllToCloudStatus(props.attachmentWellViewState, SaveAllToCloudStatus.InProgress);
        logUsage('AttachmentWellSaveAllToCloud', [
            props.attachmentWellViewState.docViewAttachments.length +
                props.attachmentWellViewState.imageViewAttachments.length,
            props.attachmentWellViewState.totalClassicAttachmentSize,
        ]);
        // prettier-ignore
        [
            operationId.current,
            subscription.current,
            notificationCallback.current,
            attachmentsToSaveToCloud.current
        ] = await saveAllToCloud(props.attachmentWellViewState, props.parentItemId, onSaveAllToCloudComplete, props.sxsId);
    };
    const onSaveAllToCloudComplete = (operationFailed: boolean) => {
        setSaveAllToCloudStatus(
            props.attachmentWellViewState,
            operationFailed ? SaveAllToCloudStatus.Failed : SaveAllToCloudStatus.Completed
        );
    };
    const onClickCancelButton = (ev: any) => {
        cancelSaveAttachmentsToCloudService(
            props.parentItemId.Id,
            getMailboxRequestOptions(props.parentItemId.mailboxInfo)
        ).then(succeeded => {
            if (succeeded) {
                lazyUnsubscribe.importAndExecute(
                    subscription.current,
                    notificationCallback.current
                );
                cancelSaveAttachmentsToCloud(operationId.current);
                attachmentsToSaveToCloud.current.forEach(attachmentId => {
                    const attachmenViewState = getAttachmentViewStateFromAttachmentWell(
                        props.attachmentWellViewState,
                        attachmentId
                    );
                    setOngoingActionAndActionMessage(
                        attachmenViewState,
                        ActionType.None,
                        ActionMessageId.SaveToCloudCanceled
                    );
                    stopFakeProgressIndicator(attachmentId, false);
                });
                setSaveAllToCloudStatus(
                    props.attachmentWellViewState,
                    SaveAllToCloudStatus.Canceled
                );
            }
        });
        logUsage('AttachmentWellCancelSaveAllToCloud', [
            props.attachmentWellViewState.docViewAttachments.length +
                props.attachmentWellViewState.imageViewAttachments.length,
            props.attachmentWellViewState.totalClassicAttachmentSize,
        ]);
    };
    const allAttachments: AttachmentFullViewState[] = props.attachmentWellViewState.imageViewAttachments.concat(
        props.attachmentWellViewState.docViewAttachments
    );
    const hasMultipleAttachments: boolean = allAttachments.length > 1;
    const isSaveAllToCloudApplicable: boolean =
        checkIsSaveAllToCloudApplicable(allAttachments) && hasMultipleAttachments;
    const buttonStyle = classNames(styles.barButton, styles.barButtonHover);
    return (
        <span>
            {shouldDisplaySaveAllToCloudButton(isSaveAllToCloudApplicable) && (
                <CommandButton className={buttonStyle} onClick={onClickSaveToCloudButton}>
                    {getSaveAllToCloudLabel()}
                </CommandButton>
            )}
            {shouldDisplayStatusLabel() && (
                <CommandButton className={buttonStyle} disabled={true}>
                    {getSaveAllToCloudStatusLabel_0()}
                </CommandButton>
            )}
            {shouldDisplayCancelButton() && (
                <CommandButton className={buttonStyle} onClick={onClickCancelButton}>
                    {loc(CancelText)}
                </CommandButton>
            )}
        </span>
    );
});

function checkIsSaveAllToCloudApplicable(attachments: AttachmentFullViewState[]) {
    return attachments.every(isSaveToCloudApplicable);
}
